import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useSiteSettingsContext } from '../components/SiteSettingsProvider';

// This hook formats a given ISO date string into localized date and time strings based on the user's
// settings and browser locale. It ensures no SSR hydration issues by initializing the date and time in
// UTC and then converting to local time in a useEffect that runs only on the client side.
// The hook accepts an object where each key represents a field name and its value is the format for that field,
// then returns an object with those same field names containing the formatted strings.
// Function overload signatures
export function useDateTimeFormat(isoString: string): { date: string; time: string };
export function useDateTimeFormat<T extends Record<string, Intl.DateTimeFormatOptions | string>>(isoString: string, formats: T): { [K in keyof T]: string };
// Implementation
export function useDateTimeFormat<T extends Record<string, Intl.DateTimeFormatOptions | string>>(isoString: string, formats?: T): { [K in keyof T]: string } {
    const defaultLanguage = useSiteSettingsContext().defaultLanguage;

    // Default formats if none provided
    const defaultFormats = formats || {
        date: DateTime.DATE_HUGE,
        time: DateTime.TIME_24_SIMPLE
    };

    // Memoize the formatting function to prevent unnecessary re-creations
    const getFormattedValue = useCallback(
        (format: Intl.DateTimeFormatOptions | string, useLocal = false) => {
            const dateTime = DateTime.fromISO(isoString);
            const dt = (useLocal ? dateTime.toLocal() : dateTime.toUTC()).setLocale(defaultLanguage);
            if (typeof format === 'string') {
                return dt.toFormat(format);
            }
            return dt.toLocaleString(format);
        },
        [defaultLanguage, isoString]
    );

    const updateFormattedValues = useCallback(
        (useLocal = false) => {
            const updated: any = {};
            for (const [key, format] of Object.entries(defaultFormats)) {
                updated[key] = getFormattedValue(format, useLocal);
            }
            return updated;
        },
        [defaultFormats, getFormattedValue]
    );

    // Initialize state for each field
    const [formattedValues, setFormattedValues] = useState(() => updateFormattedValues());

    // Update formatted values when switching to local time (runs only on client side)
    useEffect(() => {
        setFormattedValues(() => updateFormattedValues(true));
    }, [isoString]);

    return formattedValues;
}
